import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Loader2, Search } from 'lucide-react';

import getSearchUrl from '../../../utils/getSearchUrl';
import formatPhone from '../../../utils/formatPhoneForUser';
import CustomerAvatar from '@/components/customers/CustomerAvatar';
import { usePhoneMask } from '@/hooks/usePhoneMask';
import {
	Command,
	CommandInput,
	CommandList,
	CommandItem,
	CommandGroup,
} from '@/components/ui/command';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DELAY_MS = 250;

const contactInfo = (customer, maskPhone) => {
	if (customer.phone) return maskPhone(formatPhone(customer.phone));
	if (customer.email) return customer.email;
	if (customer.facebookUserId) return 'Facebook';
	if (customer.instagramUserId) return 'Instagram';
	if (customer.googleConversationId) return 'Google Business Messages';
	return '';
};

const StatusRow = ({ children }) => (
	<div className="flex items-center justify-center gap-2 px-3 py-6 text-center text-[13px] text-muted-foreground">
		{children}
	</div>
);

/**
 * Picks a customer to tag on a team message. Results come from the customer
 * search service, so the list isn't filtered again here; each row has its own
 * value so only the hovered or keyboard-selected row is highlighted.
 */
const CustomerSearch = ({
	handleSetAttachedCustomerChip,
	isEnterpriseEnabled,
	setOpenCustomerSearch,
}) => {
	const maskPhone = usePhoneMask();
	const businessGroup = useSelector((state) => state.businessGroup) || [];
	const [searchTerm, setSearchTerm] = useState('');
	const [results, setResults] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [hasSearched, setHasSearched] = useState(false);
	const latestRequest = useRef(0);

	const trimmed = searchTerm.trim();
	const isLongEnough = trimmed.replace(/[\W_]+/g, '').length >= MIN_SEARCH_LENGTH;

	useEffect(() => {
		if (!isLongEnough) {
			latestRequest.current += 1;
			setResults([]);
			setIsLoading(false);
			setHasSearched(false);
			return undefined;
		}

		// only the newest search updates the list, so slow replies can't overwrite it
		const request = latestRequest.current + 1;
		latestRequest.current = request;
		setIsLoading(true);

		const timer = setTimeout(async () => {
			try {
				const res = await axios(
					`${getSearchUrl(window.location.href)}/api/customer-search`,
					{
						method: 'post',
						data: {
							searchTerm: trimmed,
							isEnterpriseRequested: isEnterpriseEnabled,
						},
						withCredentials: true,
					}
				);

				if (latestRequest.current !== request) return;

				setResults(
					(res.data?.matchingCustomers || []).filter((customer) => customer._id)
				);
			} catch (err) {
				console.log(err);
				if (latestRequest.current === request) setResults([]);
			} finally {
				if (latestRequest.current === request) {
					setIsLoading(false);
					setHasSearched(true);
				}
			}
		}, SEARCH_DELAY_MS);

		return () => clearTimeout(timer);
	}, [trimmed, isLongEnough, isEnterpriseEnabled]);

	const selectCustomer = (customer) => {
		handleSetAttachedCustomerChip({
			_customer: customer._id,
			name: customer.name,
			phone: customer.phone,
			email: customer.email,
		});
		setSearchTerm('');
		setOpenCustomerSearch(false);
	};

	const renderStatus = () => {
		if (!isLongEnough) {
			return (
				<StatusRow>
					<Search className="size-4" />
					Type at least 3 characters to search
				</StatusRow>
			);
		}

		if (isLoading && !results.length) {
			return (
				<StatusRow>
					<Loader2 className="size-4 animate-spin" />
					Searching…
				</StatusRow>
			);
		}

		if (hasSearched && !results.length) {
			return <StatusRow>No customers match “{trimmed}”</StatusRow>;
		}

		return null;
	};

	return (
		<Command shouldFilter={false} loop className="rounded-xl">
			<CommandInput
				placeholder="Search by name, phone or email"
				value={searchTerm}
				onValueChange={setSearchTerm}
				enterKeyHint="search"
			/>
			<CommandList className="max-h-80 max-md:max-h-none">
				{renderStatus()}
				{results.length > 0 && (
					<CommandGroup heading="Customers" className="p-1.5">
						{results.map((customer) => {
							const contact = contactInfo(customer, maskPhone);
							const name = maskPhone(customer.name) || contact || 'No name';
							// searches across locations say which location the customer is at
							const location =
								isEnterpriseEnabled && customer._business
									? businessGroup.find((b) => b._id === customer._business)?.name
									: null;

							return (
								<CommandItem
									key={customer._id}
									value={customer._id}
									onSelect={() => selectCustomer(customer)}
									className="cursor-pointer gap-3 rounded-lg px-2 py-2 data-[selected=true]:bg-stone-100 dark:data-[selected=true]:bg-stone-800"
								>
									<CustomerAvatar
										customer={customer}
										fallbackName={name}
										className="size-9 shrink-0"
										fallbackClassName="text-xs font-normal"
									/>
									<div className="min-w-0 flex-1">
										<p className="truncate text-[15px] leading-5 text-foreground">
											{name}
										</p>
										{contact && contact !== name && (
											<p className="truncate text-[13px] leading-5 text-muted-foreground">
												{contact}
											</p>
										)}
										{location && (
											<p className="truncate text-xs leading-[18px] text-muted-foreground">
												{location}
											</p>
										)}
									</div>
								</CommandItem>
							);
						})}
					</CommandGroup>
				)}
			</CommandList>
		</Command>
	);
};

export default CustomerSearch;
